import {useCountry, useCustomer} from '@resurs-internal/rb-app-api';
import {
  FeatureToggle,
  isEnabled as isEnabledInCountry,
  startUnleash,
  unleash,
} from '../services/unleash';
import {useSuspenseQuery} from '@tanstack/react-query';

export const useFeatureFlags = () => {
  const country = useCountry();
  const code = country.code;
  const customer = useCustomer();
  const customerId = customer.info?.customerId;
  // Pass the country directly: the unleash query is restored from the
  // persisted cache on reload, so its context may not have countryCode yet
  const isEnabled = (toggle: FeatureToggle) => isEnabledInCountry(toggle, code);

  useSuspenseQuery({
    queryKey: ['unleash', code, customerId],
    queryFn: async () => {
      await unleash.updateContext({
        userId: customerId ? `${code}_${customerId}` : undefined,
        properties: {
          ...unleash.getContext().properties,
          countryCode: code,
        },
      });
      await startUnleash();
      return true;
    },
  });

  return {
    // kill switch
    documents: isEnabled('mp-documents'),
    savingsWithdrawal: isEnabled('mp-savings-withdrawal'),
    cardPin: isEnabled('mp-card-pin'),
    cardPanCVC: isEnabled('mp-card-pan-cvc'),
    card3dsPassword: isEnabled('mp-card-3ds-password'),
    creditAccountPayment:
      code === 'SE' && isEnabled('mp-credit-account-payment'),
    statements: isEnabled('mp-statements'),
    paymentFreeMonth: isEnabled('mp-payment-free-month'),
    bonusPointShop: isEnabled('mp-bonus-point-shop'),
    pinkTheme: isEnabled('mp-theme-pink-ribbon'),
    // country specific features
    expenseProtectionInsurance: code === 'SE',
    exchangeRateConsent: code === 'SE',
    digitalInvoiceConsent: code === 'SE' || code === 'FI',
    manageConnectedAccount: code === 'SE',
    zeroStatementFee: code === 'SE',
    offerLegalWarning: code === 'SE', // regulatory requirement in sweden
    statementDueDate: code === 'SE', // dueDate and minimum amount is incorrect for DK/FI/NO
    // enables the extended BankID check, if the user has risk level medium on their login JWT
    extendedBankIdEnabled:
      code === 'SE' && isEnabled('mp-card-activation-extended-bank-id-enabled'),
    // overrides the requirement of substantial risk medium for showing the extended BankID step during card activation
    forceExtendedBankId: isEnabled(
      'mp-card-activation-extended-bank-id-forced',
    ),
    // new features
    consentV2: isEnabled('mp-consent-v2'),
    savingsWithdrawalReceiver: isEnabled('mp-savings-withdrawal-receiver'),
    savingsWithdrawalReceiverLink: isEnabled(
      'mp-savings-withdrawal-receiver-link',
    ),
    savingsInternalTransfer: isEnabled('mp-savings-internal-transfer'),
    savingsStatements: code === 'NO' && isEnabled('mp-savings-statements'),
    savingsOnboarding: isEnabled('mp-savings-onboarding'),
    savingsDepositFunds: isEnabled('mp-savings-deposit-funds'),
    savingsDepositFundsDirectTransfer: isEnabled(
      'mp-savings-deposit-funds-direct-transfer',
    ),
    savingsDepositFundsBankTransfer: isEnabled(
      'mp-savings-deposit-funds-bank-transfer',
    ),
    savingsDepositFundsMonthly:
      code === 'SE' && isEnabled('mp-savings-deposit-funds-monthly'),
    zendesk: isEnabled('mp-zendesk'), //chat widget
    zendeskPortal: isEnabled('mp-zendesk-portal'),
    ppi: isEnabled('mp-ppi-ks'),
    familyCard: isEnabled('mp-family-card'),
    bonusCheck: isEnabled('mp-bonus-check'),
    digitalInvoiceNotification: isEnabled('mp-digital-invoice-notification'),
    digitalInvoiceNotificationForceAccept: isEnabled(
      'mp-digital-invoice-notification-force-accept',
    ),
    digitalInvoiceNotificationEvent: isEnabled(
      'mp-digital-invoice-notification-event',
    ),
    updateCustomerInformationNotification: isEnabled(
      'mp-update-customer-information-notification',
    ),
    statementPaymentConnectedAccount:
      code === 'SE' && isEnabled('mp-statement-payment-connected-account'),
    statementPaymentOpenBanking: isEnabled('mp-statement-payment-open-banking'),
    statementPaymentOpenBankingDate: isEnabled(
      'mp-statement-payment-open-banking-date',
    ),
    statementPaymentPPI: isEnabled('mp-statement-payment-ppi'),
    offersABTest: isEnabled('mp-offers-ab-test'),
    rfReturn: isEnabled('mp-bills-return'),
    rfSnooze: isEnabled('mp-bills-snooze'),
    depositAccountOfferDK: isEnabled('mp-deposit-account-offer-dk'),
    agreementCancellation: isEnabled('mp-agreement-cancellation'),
    statementSessionPayments:
      code !== 'SE' && isEnabled('mp-statement-session-payments'),
    billSessionPayments: isEnabled('mp-bill-payment-session-payments'),
    discoverV2: isEnabled('mp-discover-v2'),
    closeAccountRequest: isEnabled('mp-close-account-request'),
    billsPayment: isEnabled('mp-bills-payment'),
    trustpilotReview: isEnabled('mp-trustpilot-review'),
    billsConversion: isEnabled('mp-bills-conversion'),
    newVersionButton: isEnabled('mp-new-version-button'),
  };
};
